import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { WidgetCard } from "@/components/bi/WidgetCard";
import { WidgetRenderer } from "@/components/bi/WidgetRenderer";
import { Loading } from "@/components/bi/Loading";
import { applicableFilters } from "@/lib/widgetCatalog";
import api from "@/lib/api";
import { X } from "lucide-react";

const RGL = WidthProvider(Responsive);

export default function Tv() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const ids = useMemo(() => (params.get("ids") || "").split(",").filter(Boolean), [params]);
  const interval = Math.max(10, Number(params.get("interval")) || 30);
  const [idx, setIdx] = useState(0);
  const [dash, setDash] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    if (!ids.length) return;
    setDash(null);
    api.get(`/dashboards/${ids[idx % ids.length]}`).then(({ data }) => setDash(data)).catch(() => setDash(null));
  }, [ids, idx]);

  useEffect(() => {
    const t = setInterval(() => {
      if (ids.length > 1) setIdx((i) => (i + 1) % ids.length);
      else setRefreshToken((r) => r + 1);
    }, interval * 1000);
    return () => clearInterval(t);
  }, [ids, interval]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") nav("/"); };
    window.addEventListener("keydown", onKey);
    document.documentElement.requestFullscreen?.().catch(() => {});
    return () => { window.removeEventListener("keydown", onKey); document.exitFullscreen?.().catch(() => {}); };
  }, [nav]);

  const widgets = useMemo(() => dash?.widgets || [], [dash]);
  const gridLayout = useMemo(() => widgets.map((w) => ({ i: w.id, x: w.layout?.x || 0, y: w.layout?.y || 0, w: w.layout?.w || 6, h: w.layout?.h || 5 })), [widgets]);

  if (!ids.length) return <div className="min-h-screen flex items-center justify-center bi-canvas text-sm text-[color:var(--ink-3)]">No dashboards selected for TV mode.</div>;

  return (
    <div data-testid="tv-mode" className="min-h-screen bi-canvas">
      <div className="fixed top-3 right-3 z-50 flex items-center gap-2">
        {ids.length > 1 && (
          <span className="text-[11px] px-2 py-1 rounded-full bg-black/40 text-white backdrop-blur-sm">
            {(idx % ids.length) + 1} / {ids.length} · {interval}s
          </span>
        )}
        <button data-testid="tv-exit" onClick={() => nav("/")} className="w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60" title="Exit (Esc)">
          <X className="w-4 h-4" />
        </button>
      </div>
      {!dash ? <div className="min-h-screen flex items-center justify-center"><Loading label="Loading dashboard" /></div> : (
        <>
          <div className="px-6 pt-4"><h1 className="font-display text-2xl text-[color:var(--ink)]">{dash.name}</h1></div>
          <RGL className="layout" layouts={{ lg: gridLayout, md: gridLayout, sm: gridLayout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={56} margin={[16, 16]} containerPadding={[16, 16]}
            isDraggable={false} isResizable={false}>
            {widgets.map((w) => (
              <div key={w.id}>
                <WidgetCard title={w.title || undefined}>
                  <WidgetRenderer widget={w} globalFilters={applicableFilters(w, [])} refreshToken={refreshToken} pivotEngine={dash?.canvas?.pivot_engine} chartFont={dash?.canvas?.chart_font} lang={dash?.canvas?.language} />
                </WidgetCard>
              </div>
            ))}
          </RGL>
        </>
      )}
    </div>
  );
}
